import React, { FC, Fragment, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddDentistFormComponent from './add-dentist-form.component';
import { User } from 'models/user';
import { useFormik } from 'formik';
import { useLazyQuery } from '@apollo/client';
import { getUserByEmailTemplate } from 'apollo/templates/user';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { LoadingButton } from '@mui/lab';
import { ClinicDropdown, OrgDropdown } from 'models/user';
import LinkUserFormComponent from '../../shared/link-user-form.component';
import ClinicsAutocomplete from '../clinics-autocomplete/clinics-autocomplete.component';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import { permissions } from 'utils/permission';

type AddDentistDialogProps = {
    dialogOpen: boolean;
    onDialogClose: () => void;
    onUserAdded: (user: User) => void;
    onUserLinked?: (user: User) => void;
};

const AddDentistDialog: FC<AddDentistDialogProps> = ({
    dialogOpen,
    onDialogClose,
    onUserAdded,
    onUserLinked,
}: AddDentistDialogProps) => {
    const { t: translate } = useTranslation('users');
    const [activeStep, setActiveStep] = useState(0);
    const [notAllowedMsg, setNotAllowedMsg] = useState('');
    const [user, setUser] = useState<User>({} as User);
    const { showBoundary } = useDenthubErrorBoundary();

    const [getUserByEmail, { loading }] = useLazyQuery(getUserByEmailTemplate, {
        onCompleted: ({ userByEmail: user }) => {
            if (!user) {
                setActiveStep(1);
                return;
            }
            setUser(user);
            if (user.organization?._id === formik.values.organization?._id) {
                // User already exists in the selected clinic
                setActiveStep(3);
                return;
            }
            if (user.isShared) {
                // No allowed
                setNotAllowedMsg('NOT_ALLOWED_SHARED');
                setActiveStep(4);
                return;
            }
            if (
                permissions.isDentist(user.type) ||
                permissions.isLabTechnician(user.type) ||
                permissions.isExternalTechnician(user.role)
            ) {
                // Link
                const hasIsolatedUsers = customerSettings.getCustomerSetting(
                    user.organization.customerSettings || [],
                    CONSTANTS.SETTINGS_NAMES.ISOLATED_USERS,
                );
                if (hasIsolatedUsers) {
                    // No allowed
                    setNotAllowedMsg('NOT_ALLOWED_CLINIC');
                    setActiveStep(4);
                    return;
                }
                setActiveStep(2);
                return;
            }
            // No allowed
            setNotAllowedMsg('NOT_ALLOWED');
            setActiveStep(4);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    type LookupValues = { email: string; organization: ClinicDropdown | null };

    const validate = (values: LookupValues) => {
        const errors: Record<string, string> = {};
        if (!values.email) {
            errors.email = translate('users:VALIDATION:EMAIL');
        }
        if (!values.organization) {
            errors.organization = translate('organization:VALIDATION:PARENT_CLINIC');
        }
        return errors;
    };

    const formik = useFormik<LookupValues>({
        initialValues: {
            email: '',
            organization: null,
        },
        validate,
        onSubmit: (values) => {
            getUserByEmail({ variables: { email: values.email } });
        },
    });

    const moveToFirstStep = () => {
        setActiveStep(0);
    };

    const userLinked = (user: User) => {
        onDialogClose();
        if (onUserLinked) {
            onUserLinked(user);
        }
    };

    const getStepContent = (stepIndex: number) => {
        switch (stepIndex) {
            case 0:
                return (
                    <Fragment>
                        <form onSubmit={formik.handleSubmit}>
                            <DialogContent>
                                <FormControl fullWidth>
                                    <ClinicsAutocomplete
                                        onParentClinicChange={(clinic) =>
                                            formik.setFieldValue('organization', clinic ?? null)
                                        }
                                        value={formik.values.organization}
                                        name="organization"
                                        onBlur={formik.handleBlur}
                                        helperText={formik.touched.organization && formik.errors.organization}
                                        error={formik.touched.organization && Boolean(formik.errors.organization)}
                                        fullWidth
                                        useInForm
                                        required
                                    />
                                </FormControl>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        id="email"
                                        label={translate('common:EMAIL')}
                                        required
                                        type="email"
                                        helperText={formik.touched.email && formik.errors.email}
                                        error={formik.touched.email && Boolean(formik.errors.email)}
                                        {...formik.getFieldProps('email')}
                                    />
                                </FormControl>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => onDialogClose()}>{translate('common:CANCEL')}</Button>
                                <LoadingButton type="submit" variant="contained" loading={loading}>
                                    {translate('common:NEXT_STEP')}
                                </LoadingButton>
                            </DialogActions>
                        </form>
                    </Fragment>
                );
            case 1:
                return (
                    <AddDentistFormComponent
                        onUserAdded={onUserAdded}
                        onDialogClose={onDialogClose}
                        email={formik.values.email}
                        organization={formik.values.organization}
                    />
                );
            case 2:
                return (
                    <LinkUserFormComponent
                        userType="DENTIST"
                        user={user}
                        organization={formik.values.organization as OrgDropdown}
                        onBack={moveToFirstStep}
                        onUserLinked={userLinked}
                    />
                );
            case 3:
                return (
                    <DialogContent>
                        <Box>
                            <Alert severity="warning">{translate(`users:USER_ALREADY_EXISTS`)}</Alert>
                        </Box>
                        <DialogActions>
                            <Button onClick={() => onDialogClose()}>{translate('common:CANCEL')}</Button>
                            <Button onClick={moveToFirstStep} variant="contained">
                                {translate('common:BACK')}
                            </Button>
                        </DialogActions>
                    </DialogContent>
                );
            case 4:
                return (
                    <DialogContent>
                        <Box>
                            <Alert severity="warning">{translate(`users:${notAllowedMsg}`)}</Alert>
                        </Box>
                        <DialogActions>
                            <Button onClick={() => onDialogClose()}>{translate('common:CANCEL')}</Button>
                            <Button onClick={moveToFirstStep} variant="contained">
                                {translate('common:BACK')}
                            </Button>
                        </DialogActions>
                    </DialogContent>
                );
            default:
                return 'Unknown Step';
        }
    };

    useEffect(() => {
        // Reset on dialog open
        if (dialogOpen) {
            formik.resetForm();
            setNotAllowedMsg('');
            setUser({} as User);
            setActiveStep(0);
        }
    }, [dialogOpen]);

    return (
        <Dialog maxWidth="md" fullWidth open={dialogOpen} onClose={onDialogClose}>
            <DialogTitle>{translate('users:ADD_DENTIST')}</DialogTitle>
            {getStepContent(activeStep)}
        </Dialog>
    );
};

export default AddDentistDialog;
